import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";
import Toast from "./Toast";
import ProfileEditModal from "./ProfileEditModal";
import PasswordChangeModal from "./PasswordChangeModal";
import ConfirmationModal from "./ConfirmationModal";
import { User } from "lucide-react";

export default function ProfileView() {
  const [showToast, setShowToast] = useState<{ message: string; type: "success" | "info" } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "Dr. Arjun Mehta",
    role: "Senior Quality Engineer",
    email: "arjun.mehta@rmcqualityapp.com",
    phone: "+91 98765 43210",
    employeeId: "QE-2847",
    department: "Quality Assurance",
    joinDate: "Jan 15, 2022",
    certifications: [
      "ACI Concrete Field Testing Technician - Grade I",
      "NICET Level II - Materials Testing",
      "ISO 9001:2015 Lead Auditor",
    ],
  });

  const handleEditProfile = () => {
    setShowEditModal(true);
  };

  const handleSaveProfile = (data: typeof profileData) => {
    setProfileData(data);
    setShowToast({ message: "Profile updated successfully", type: "success" });
  };

  const handleDownloadProfile = () => {
    setShowToast({ message: "Downloading profile data...", type: "info" });
    setTimeout(() => {
      setShowToast({ message: "Profile downloaded as PDF", type: "success" });
    }, 1000);
  };

  const handleShareProfile = () => {
    setShowToast({ message: "Sharing profile...", type: "success" });
  };

  const handleChangePassword = () => {
    setShowPasswordModal(true);
  };

  const handlePasswordSuccess = () => {
    setShowToast({ message: "Password changed successfully", type: "success" });
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setShowToast({ message: "Logging out...", type: "info" });
  };

  const stats = [
    { label: "Batches Tested", value: "2,847", trend: "+12%" },
    { label: "Reports Generated", value: "1,423", trend: "+8%" },
    { label: "Pass Rate", value: "98.3%", trend: "+2.1%" },
  ];

  const recentActivity = [
    { action: "Approved batch RMC-2847", time: "2 min ago" },
    { action: "Generated quality report", time: "1 hour ago" },
    { action: "Reviewed delivery TN-01-AB-1234", time: "3 hours ago" },
    { action: "Completed 28-day test analysis", time: "1 day ago" },
  ];

  return (
    <div className="pb-24 bg-[#F9FAFB]">
      {/* Header with gradient background */}
      <div
        className="px-5 pt-6 pb-28 mb-[-68px] relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1B2A5B 0%, #2A3B6F 50%, #1B2A5B 100%)",
        }}
      >
        {/* Subtle pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)",
          }}
        />
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <div className="flex items-center justify-between mb-6">
            <h1
              className="text-white"
              style={{
                fontSize: "28px",
                letterSpacing: "-0.025em",
                fontWeight: "700",
              }}
            >
              Profile
            </h1>
            <motion.button
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.02 }}
              onClick={handleEditProfile}
              className="px-4 py-2 rounded-xl text-white backdrop-blur-xl transition-smooth"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                fontSize: "13px",
                fontWeight: "600",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              }}
            >
              Edit
            </motion.button>
          </div>

          {/* Profile Picture & Name */}
          <div className="flex items-center gap-4">
            <div
              className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-xl flex items-center justify-center"
              style={{
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
            >
              <User size={36} className="text-white" />
            </div>
            <div>
              <div
                className="text-white mb-1"
                style={{
                  fontSize: "22px",
                  letterSpacing: "-0.02em",
                  fontWeight: "700",
                }}
              >
                {profileData.name}
              </div>
              <div
                className="text-white/80"
                style={{ fontSize: "14px", fontWeight: "500" }}
              >
                {profileData.role}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats Cards - with proper z-index to appear above header */}
      <div className="px-5 mb-5 relative z-20">
        <div className="grid grid-cols-3 gap-3">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.5 }}
              className="bg-white rounded-2xl p-4 card-premium"
              style={{
                boxShadow:
                  "0 2px 4px rgba(10, 15, 28, 0.04), 0 1px 2px rgba(10, 15, 28, 0.02)",
                border: "1px solid rgba(0, 0, 0, 0.04)",
              }}
            >
              <div
                className="text-[#717182] mb-2 label-subtle"
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.05em",
                  fontWeight: "600",
                }}
              >
                {stat.label.toUpperCase()}
              </div>
              <div
                className="text-[#0A0F1C] mb-1"
                style={{
                  fontSize: "20px",
                  letterSpacing: "-0.02em",
                  fontWeight: "700",
                }}
              >
                {stat.value}
              </div>
              <div
                className="text-[#0F3D2E]"
                style={{ fontSize: "11px", fontWeight: "600" }}
              >
                {stat.trend}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Personal Information */}
      <div className="px-5 mb-5">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-white rounded-3xl p-5"
          style={{
            boxShadow:
              "0 2px 4px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div
            className="text-[#717182] mb-5"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              fontWeight: "600",
            }}
          >
            PERSONAL INFORMATION
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1B2A5B]08 flex items-center justify-center flex-shrink-0">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="text-[#1B2A5B]"
                >
                  <path
                    d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M3 7h14M7 10h6M7 13h4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div
                  className="text-[#717182] mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.02em" }}
                >
                  Employee ID
                </div>
                <div
                  className="text-[#0A0F1C]"
                  style={{ fontSize: "15px", fontWeight: "600" }}
                >
                  {profileData.employeeId}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1B2A5B]08 flex items-center justify-center flex-shrink-0">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="text-[#1B2A5B]"
                >
                  <path
                    d="M3 6l7-3 7 3v8l-7 3-7-3V6z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div
                  className="text-[#717182] mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.02em" }}
                >
                  Department
                </div>
                <div
                  className="text-[#0A0F1C]"
                  style={{ fontSize: "15px", fontWeight: "600" }}
                >
                  {profileData.department}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1B2A5B]08 flex items-center justify-center flex-shrink-0">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="text-[#1B2A5B]"
                >
                  <rect
                    x="3"
                    y="4"
                    width="14"
                    height="12"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M3 8h14M7 1v3M13 1v3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div
                  className="text-[#717182] mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.02em" }}
                >
                  Join Date
                </div>
                <div
                  className="text-[#0A0F1C]"
                  style={{ fontSize: "15px", fontWeight: "600" }}
                >
                  {profileData.joinDate}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1B2A5B]08 flex items-center justify-center flex-shrink-0">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="text-[#1B2A5B]"
                >
                  <rect
                    x="3"
                    y="5"
                    width="14"
                    height="11"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M3 8l7 4 7-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div
                  className="text-[#717182] mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.02em" }}
                >
                  Email
                </div>
                <div
                  className="text-[#0A0F1C]"
                  style={{ fontSize: "14px", fontWeight: "500" }}
                >
                  {profileData.email}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1B2A5B]08 flex items-center justify-center flex-shrink-0">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="text-[#1B2A5B]"
                >
                  <path
                    d="M3 5a2 2 0 012-2h2.5a1 1 0 011 .8l.5 2a1 1 0 01-.3 1l-1.5 1.2a11 11 0 005.8 5.8l1.2-1.5a1 1 0 011-.3l2 .5a1 1 0 01.8 1V15a2 2 0 01-2 2A14 14 0 013 5z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div
                  className="text-[#717182] mb-1"
                  style={{ fontSize: "11px", letterSpacing: "0.02em" }}
                >
                  Phone
                </div>
                <div
                  className="text-[#0A0F1C]"
                  style={{ fontSize: "15px", fontWeight: "600" }}
                >
                  {profileData.phone}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Certifications */}
      <div className="px-5 mb-5">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="bg-white rounded-3xl p-5"
          style={{
            boxShadow:
              "0 2px 4px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div
            className="text-[#717182] mb-5"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              fontWeight: "600",
            }}
          >
            CERTIFICATIONS
          </div>

          <div className="space-y-3">
            {profileData.certifications.map((cert, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 rounded-xl bg-[#F9FAFB]"
              >
                <div
                  className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1B2A5B] to-[#2A3B6F] flex items-center justify-center flex-shrink-0"
                  style={{
                    boxShadow: "0 2px 8px rgba(27, 42, 91, 0.25)",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="text-white"
                  >
                    <path
                      d="M13 4L6 11 3 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div
                  className="text-[#0A0F1C] flex-1"
                  style={{ fontSize: "13px", fontWeight: "500", lineHeight: "1.4" }}
                >
                  {cert}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <div className="px-5 mb-5">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="bg-white rounded-3xl p-5"
          style={{
            boxShadow:
              "0 2px 4px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div
            className="text-[#717182] mb-5"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              fontWeight: "600",
            }}
          >
            RECENT ACTIVITY
          </div>

          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start gap-3">
                <div
                  className="w-2 h-2 rounded-full bg-[#1B2A5B] mt-2 flex-shrink-0"
                  style={{ opacity: 1 - index * 0.2 }}
                />
                <div className="flex-1">
                  <div
                    className="text-[#0A0F1C] mb-1"
                    style={{ fontSize: "14px", fontWeight: "500" }}
                  >
                    {activity.action}
                  </div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "12px" }}
                  >
                    {activity.time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Actions */}
      <div className="px-5 mb-5">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="bg-white rounded-3xl p-5"
          style={{
            boxShadow:
              "0 2px 4px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div
            className="text-[#717182] mb-5"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              fontWeight: "600",
            }}
          >
            ACTIONS
          </div>

          <div className="space-y-4">
            <button
              className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] font-medium"
              onClick={handleDownloadProfile}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="text-[#1B2A5B]"
              >
                <path
                  d="M13 8v4M13 12l-4 4-4-4M13 12l4 4 4-4M13 8h-2v-2h2v2z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Download Profile
            </button>

            <button
              className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] font-medium"
              onClick={handleShareProfile}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="text-[#1B2A5B]"
              >
                <path
                  d="M13 8v4M13 12l-4 4-4-4M13 12l4 4 4-4M13 8h-2v-2h2v2z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Share Profile
            </button>

            <button
              className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] font-medium"
              onClick={handleChangePassword}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="text-[#1B2A5B]"
              >
                <path
                  d="M13 8v4M13 12l-4 4-4-4M13 12l4 4 4-4M13 8h-2v-2h2v2z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Change Password
            </button>

            <button
              className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] font-medium"
              onClick={handleLogout}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="text-[#1B2A5B]"
              >
                <path
                  d="M13 8v4M13 12l-4 4-4-4M13 12l4 4 4-4M13 8h-2v-2h2v2z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Logout
            </button>
          </div>
        </motion.div>
      </div>

      {/* Edit Modal */}
      <ProfileEditModal
        isOpen={showEditModal}
        profileData={profileData}
        onSave={handleSaveProfile}
        onClose={() => setShowEditModal(false)}
      />

      {/* Password Modal */}
      <PasswordChangeModal
        isOpen={showPasswordModal}
        onSuccess={handlePasswordSuccess}
        onClose={() => setShowPasswordModal(false)}
      />

      {/* Logout Modal */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        onConfirm={handleLogoutConfirm}
        onClose={() => setShowLogoutModal(false)}
        icon={
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            className="text-[#1B2A5B]"
          >
            <path
              d="M11 8H6a2 2 0 00-2 2v12a2 2 0 002 2h5M16 28l6-6-6-6M10 16h12\"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        }
      />

      {/* Toast */}
      {showToast && (
        <Toast
          message={showToast.message}
          type={showToast.type}
          onClose={() => setShowToast(null)}
        />
      )}
    </div>
  );
}